"use client";

/* eslint-disable react/require-default-props */
import { getConvertedJpeg } from "api/get-converted-jpeg.api";
import useScreenSize from "hoc/useScreenSize";
import WebpContext from "hooks-contexts/WebpContext";
import useSafeEffect from "hooks-contexts/useSafeEffect";
import Image from "next/legacy/image";
import { memo, useContext, useState } from "react";
import { getAbsoluteImagePath } from "util/helper/getAbsoluteImagePath";
import { getRelativeImagePath } from "../../../util/helper/getRelativeImagePath";

interface IProps {
  width?: number;
  height?: number;
  desktopHeight?: string;
  mobileHeight?: string;
  tabletHeight?: string;
  src: string;
  alt: string;
  style?: any;
  className?: string;
  fallBackImage?: string;
  isNextImageComponent?: boolean;
  handleFallbackImage?: () => void;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  objectPosition?: string;
}

function PAImage(props: IProps) {
  const {
    width, height, src, alt, style, className = "", desktopHeight, mobileHeight,
    tabletHeight, fallBackImage, isNextImageComponent, title, handleFallbackImage,
    objectFit, objectPosition, ...restProps
  } = props;
  const [imgSrc, setImgSrc] = useState(src);
  const { canUseWebp } = useContext(WebpContext);
  const [isWebpImageError, setIsWebpImageError] = useState(false);
  const [, isTablet, desktop] = useScreenSize();

  function loadConvertedImage() {
    const absoluteSrc = getAbsoluteImagePath(imgSrc);
    getConvertedJpeg(absoluteSrc)
      .then((base64) => {
        setImgSrc(`data:image/jpeg;base64,${base64}`);
      })
      .catch(() => {
        setImgSrc(getRelativeImagePath("/images/common/image-not-available_16051144.png"));
      });
  }

  useSafeEffect(() => {
    // throw new Error("fdnkfg");
    if (!canUseWebp && fallBackImage) {
      setImgSrc(fallBackImage);
    } else if (!canUseWebp && imgSrc?.endsWith(".webp")) {
      loadConvertedImage();
    } else {
      setImgSrc(src);
    }
  }, [src, canUseWebp]);

  useSafeEffect(() => {
    if (isWebpImageError) {
      loadConvertedImage();
    }
  }, [isWebpImageError]);

  const isCustomHeightProvided = desktopHeight || mobileHeight || tabletHeight;

  let imageHeight = isCustomHeightProvided ? mobileHeight : height;
  if (isTablet) {
    imageHeight = isCustomHeightProvided ? tabletHeight : height;
  } else if (desktop) {
    imageHeight = isCustomHeightProvided ? desktopHeight : height;
  }

  const divStyle = {
    width,
    height: imageHeight,
    position: "relative" as const,
  };

  function onImageError() {
    const fallback404Src = getRelativeImagePath("/images/common/image-not-available_16051144.png");
    if (fallBackImage && imgSrc !== fallBackImage) {
      setImgSrc(fallBackImage);
      if (handleFallbackImage) {
        handleFallbackImage();
      }
    } else if (!fallBackImage && imgSrc?.endsWith(".webp") && !isWebpImageError) {
      setIsWebpImageError(true);
    } else {
      setImgSrc(fallback404Src);
    }
  }

  if (isNextImageComponent) {
    return (
      <Image
        src={imgSrc}
        width={width}
        height={height}
        unoptimized
        onError={onImageError}
      />
    );
  }

  return (
    <div
      style={divStyle}
      className={className}
    >
      <Image
        layout="fill"
        objectFit={objectFit || "contain"}
        objectPosition={objectPosition}
        unoptimized
        {...{
          src: imgSrc, alt, style, className, title, ...restProps,
        }}
        onError={onImageError}
      />
    </div>
  );
}

export default memo(PAImage);
